import { Clipboard } from '@angular/cdk/clipboard';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { DomSanitizer } from '@angular/platform-browser';

import { CurrencyPipe } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import { QRCodeModule } from 'angularx-qrcode';
import { Subscription, interval } from 'rxjs';

import { AppConstants, Providers, QR_CODE_COPIED_MESSAGE_TIMEOUT } from '../../../../config/appConstants';
import { MessageType } from '../../../../model/message.model';
import { TimerUnits } from '../../../../model/utility.models';
import { GtmService } from '../../../../service/gtm.service';
import { ResizeService } from '../../../../service/resize.service';
import { UserDataService } from '../../../../service/userDataService';
import { MessageComponent } from '../../../../shared/components/messages/message-component';

@Component({
    selector: 'app-wallet-process',
    templateUrl: './wallet-process.component.html',
    imports: [
        QRCodeModule,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        TranslateModule,
        CurrencyPipe,
        MessageComponent,
        NgClass,
        NgTemplateOutlet,
    ],
})
export class WalletProcessComponent implements OnInit {
    private clipboard = inject(Clipboard);
    private sanitizer = inject(DomSanitizer);
    private gtmService = inject(GtmService);
    userDataService = inject(UserDataService);
    private resizeService = inject(ResizeService);

    readonly showPixPage = output<boolean>();
    depositSubmitResponse = input<any>();
    paymentMethod: string;
    timerDurationDetails = signal<TimerUnits>({ hours: '00', minutes: '00', seconds: '00' });
    intervalTimer: Subscription;
    QRcodeImage: string = '';
    showInstructions: boolean = false;
    QRCodeCopiedStatus: { [key: string]: any } = {};
    providerName: string;
    showPixQRCode: boolean = false;
    type: MessageType;
    public get messageType(): typeof MessageType {
        return MessageType;
    }
    providerNames = Providers.PROVIDER_NAMES;

    ngOnInit(): void {
        this.initialize();
    }
    initialize() {
        this.paymentMethod = this.depositSubmitResponse()?.paymentMethod;
        if (this.paymentMethod == AppConstants.PIX) {
            this.providerName = this.depositSubmitResponse()?.configData?.pixProvider;
            const { textToQRcode, base64QRcode } = this.depositSubmitResponse()?.postData || {};
            this.QRcodeImage = this.providerNames.includes(this.providerName) ? textToQRcode : this.getSanitizeImageUrl(base64QRcode);
            //QR page on load GTM event
            this.gtmService.raiseEventForBrazilBankAccountReg(
                'Copycode or QR scan page',
                'not applicable',
                'load',
                'pix',
                this.depositSubmitResponse()?.transactionId,
            );
            this.counterDownTimer();
        }
        this.resizeService.resizeCashier();
    }
    getSanitizeImageUrl(base64QRcode: string) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(`data:image/png;base64, ${base64QRcode}`);
    }
    showQRCodeCopiedNotification(source: string) {
        if (this.QRCodeCopiedStatus[source]?.curr_interval) {
            clearTimeout(this.QRCodeCopiedStatus[source]?.curr_interval);
        }
        const interval2 = setTimeout(() => {
            this.QRCodeCopiedStatus[source].isActive = false;
        }, QR_CODE_COPIED_MESSAGE_TIMEOUT);
        this.QRCodeCopiedStatus[source] = {
            isActive: true,
            curr_interval: interval2,
        };
    }
    copyContentToClipboard(source: string) {
        try {
            const data = this.depositSubmitResponse()?.postData?.textToQRcode;
            const copyText = source == 'ICON_CLICKED' ? 'copy coupon' : 'pix cta';
            this.gtmService.copyQrCodeOrCtaOnPix(copyText, this.depositSubmitResponse()?.transactionId);
            this.clipboard.copy(data);
            this.showQRCodeCopiedNotification(source);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }
    counterDownTimer() {
        const qrcodeTimerLimit = this.userDataService?.userData?.configParams?.PIX_EXPIRY_TIME_IN_SECONDS || '03-00-00';
        const [hrs, mins, secs] = qrcodeTimerLimit.trim().split('-').map(Number);

        const countdownEndTime = new Date().getTime() + hrs * 3600000 + mins * 60000 + secs * 1000;

        this.updateTimerDisplay(hrs, mins, secs);

        this.intervalTimer = interval(1000).subscribe(() => {
            const now = Date.now();
            const remaining = countdownEndTime - now;

            if (remaining <= 0) {
                this.updateTimerDisplay(0, 0, 0);
                this.intervalTimer.unsubscribe();
                return;
            }

            const hours = this.formatTime(Math.floor((remaining / (1000 * 60 * 60)) % 24));
            const minutes = this.formatTime(Math.floor((remaining / (1000 * 60)) % 60));
            const seconds = this.formatTime(Math.floor((remaining / 1000) % 60));

            this.updateTimerDisplay(hours, minutes, seconds);
        });
    }
    private formatTime(value: number): string {
        return value < 10 ? `0${value}` : value.toString();
    }
    private updateTimerDisplay(hours: number | string, minutes: number | string, seconds: number | string) {
        this.timerDurationDetails.set({
            hours: hours.toString(),
            minutes: minutes.toString(),
            seconds: seconds.toString(),
        });
    }

    expand(type: string, value: boolean) {
        (this as any)[type] = value;
        this.resizeService.resizeCashier();
    }
    goToPixProcess() {
        this.showPixPage.emit(true);
    }
}
